import type { Command } from '../types/commands';
import type { NavigationContext } from './navigation/rules/NavigationRule';
import { logger } from './utils/logger';

const RECENT_COMMANDS_KEY = 'command-palette-recent';
const MAX_RECENT = 5;

/**
 * Get recent commands from localStorage
 */
export function getRecentCommands(): Command[] {
  try {
    const stored = localStorage.getItem(RECENT_COMMANDS_KEY);
    if (!stored) return [];
    const parsed = JSON.parse(stored) as unknown;
    return Array.isArray(parsed) ? (parsed as Command[]) : [];
  } catch (error) {
    logger.warn('Failed to read recent commands from localStorage', error);
    return [];
  }
}

/**
 * Add command to recent commands list
 * Moves to front if already exists, limits to MAX_RECENT
 */
export function addToRecentCommands(command: Command): void {
  try {
    const recent = getRecentCommands();
    // Remove if already exists
    const filtered = recent.filter((c) => c.id !== command.id);
    // Add to front
    const updated = [command, ...filtered].slice(0, MAX_RECENT);
    localStorage.setItem(RECENT_COMMANDS_KEY, JSON.stringify(updated));
  } catch (error) {
    logger.warn('Failed to save recent command to localStorage', error);
  }
}

/**
 * Filter commands based on visibility rules
 * All rules must pass (AND logic)
 *
 * @param commands - Commands to filter
 * @param context - NavigationContext with hasPermission, isSystemAdmin
 */
export function filterCommandsByRules(commands: Command[], context: NavigationContext): Command[] {
  return commands.filter((cmd) => {
    if (!cmd.visibilityRules || cmd.visibilityRules.length === 0) {
      return true; // No rules = always visible
    }
    // All rules must pass (AND logic)
    return cmd.visibilityRules.every((rule) => rule.isVisible(context));
  });
}
